import { lazy, Suspense, useMemo, useRef, useState } from "react";
import { motion, useInView } from "framer-motion";
import { calculateSolar } from "@/lib/solar";

// recharts ist schwer – erst laden, wenn der Chart in Sichtweite kommt.
const AmortisationChart = lazy(() =>
  import("@/components/site/AmortisationChart")
);
import { Slider } from "@/components/ui/slider";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Sun, Battery, Car, TrendingUp, Leaf, Coins, Clock } from "lucide-react";

const CANTONS = [
  ["ZH", "Zürich"], ["BE", "Bern"], ["LU", "Luzern"], ["UR", "Uri"],
  ["SZ", "Schwyz"], ["OW", "Obwalden"], ["NW", "Nidwalden"], ["GL", "Glarus"],
  ["ZG", "Zug"], ["FR", "Freiburg"], ["SO", "Solothurn"], ["BS", "Basel-Stadt"],
  ["BL", "Basel-Land"], ["SH", "Schaffhausen"], ["AR", "Appenzell A."],
  ["AI", "Appenzell I."], ["SG", "St. Gallen"], ["GR", "Graubünden"],
  ["AG", "Aargau"], ["TG", "Thurgau"], ["TI", "Tessin"], ["VD", "Waadt"],
  ["VS", "Wallis"], ["NE", "Neuenburg"], ["GE", "Genf"], ["JU", "Jura"],
];

const fmt = (n) => new Intl.NumberFormat("de-CH").format(Math.round(n));

export default function Calculator() {
  const [roofArea, setRoofArea] = useState([60]);
  const [consumption, setConsumption] = useState([4500]);
  const [canton, setCanton] = useState("ZH");
  const [battery, setBattery] = useState(true);
  const [wallbox, setWallbox] = useState(false);
  const chartRef = useRef(null);
  const chartInView = useInView(chartRef, { once: true, margin: "300px" });
  const result = useMemo(
    () =>
      calculateSolar({
        roof_area: roofArea[0],
        annual_consumption: consumption[0],
        canton,
        has_battery: battery,
        has_wallbox: wallbox,
      }),
    [roofArea, consumption, canton, battery, wallbox]
  );

  return (
    <section
      id="rechner"
      data-testid="calculator-section"
      className="relative py-20 md:py-28 bg-[#FAF7EF]"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="max-w-2xl mb-12">
          <div className="text-sm text-[#EAB308] font-semibold uppercase tracking-widest">
            Solarrechner
          </div>
          <h2 className="mt-3 font-display font-semibold text-3xl sm:text-4xl lg:text-5xl tracking-tight text-[#1A1D20]">
            Wie viel sparen Sie mit Solar?
          </h2>
          <p className="mt-4 text-[#4B5563]">
            Schieben Sie die Regler – wir berechnen sofort Ihre Ersparnis,
            CO₂-Reduktion und Amortisationszeit. Unverbindlich, in Echtzeit.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          {/* Inputs */}
          <div className="lg:col-span-7 bg-white border border-black/5 rounded-2xl p-6 md:p-8">
            <div className="space-y-8">
              <div>
                <div className="flex items-end justify-between mb-3">
                  <Label className="text-[#1A1D20] font-medium">
                    Verfügbare Dachfläche
                  </Label>
                  <span className="font-display font-bold text-xl text-[#1A1D20]">
                    {roofArea[0]} m²
                  </span>
                </div>
                <Slider
                  data-testid="calc-slider-area"
                  value={roofArea}
                  onValueChange={setRoofArea}
                  min={10}
                  max={500}
                  step={5}
                />
                <div className="flex justify-between text-xs text-[#4B5563] mt-2">
                  <span>10 m²</span><span>500 m²</span>
                </div>
              </div>

              <div>
                <div className="flex items-end justify-between mb-3">
                  <Label className="text-[#1A1D20] font-medium">
                    Stromverbrauch pro Jahr
                  </Label>
                  <span className="font-display font-bold text-xl text-[#1A1D20]">
                    {fmt(consumption[0])} kWh
                  </span>
                </div>
                <Slider
                  data-testid="calc-slider-consumption"
                  value={consumption}
                  onValueChange={setConsumption}
                  min={1000}
                  max={50000}
                  step={500}
                />
                <div className="flex justify-between text-xs text-[#4B5563] mt-2">
                  <span>1&apos;000</span><span>50&apos;000</span>
                </div>
              </div>

              <div>
                <Label className="text-[#1A1D20] font-medium mb-3 block">
                  Kanton
                </Label>
                <Select value={canton} onValueChange={setCanton}>
                  <SelectTrigger data-testid="calc-canton-trigger" className="h-12 rounded-xl">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {CANTONS.map(([c, name]) => (
                      <SelectItem key={c} value={c} data-testid={`calc-canton-${c}`}>
                        {name} ({c})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="flex items-center justify-between p-4 rounded-xl border border-black/5 bg-[#FDFCF9]">
                  <div className="flex items-center gap-3">
                    <Battery size={18} className="text-[#EAB308]" />
                    <div>
                      <div className="text-sm font-medium text-[#1A1D20]">Stromspeicher</div>
                      <div className="text-xs text-[#4B5563]">+ Eigenverbrauch</div>
                    </div>
                  </div>
                  <Switch
                    data-testid="calc-switch-battery"
                    checked={battery}
                    onCheckedChange={setBattery}
                  />
                </div>
                <div className="flex items-center justify-between p-4 rounded-xl border border-black/5 bg-[#FDFCF9]">
                  <div className="flex items-center gap-3">
                    <Car size={18} className="text-[#EAB308]" />
                    <div>
                      <div className="text-sm font-medium text-[#1A1D20]">Wallbox</div>
                      <div className="text-xs text-[#4B5563]">PV-Überschuss laden</div>
                    </div>
                  </div>
                  <Switch
                    data-testid="calc-switch-wallbox"
                    checked={wallbox}
                    onCheckedChange={setWallbox}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Results - Dark control room */}
          <div
            className="lg:col-span-5 bg-[#1A1D20] rounded-2xl p-6 md:p-8 text-white relative overflow-hidden"
            data-testid="calc-results-panel"
          >
            <div className="absolute -top-20 -right-20 w-64 h-64 sun-ring opacity-60" />
            <div className="relative">
              <div className="flex items-center gap-2 text-[#EAB308] text-xs uppercase tracking-widest font-semibold">
                <Sun size={14} /> Ihr Ergebnis
              </div>

              <motion.div
                key={result?.system_size_kwp}
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                className="mt-4"
              >
                <div className="text-xs text-white/50 uppercase tracking-wider">Anlagengrösse</div>
                <div className="font-display font-bold text-5xl tracking-tight">
                  {result ? result.system_size_kwp.toFixed(1) : "–"}
                  <span className="text-2xl text-white/60 font-medium ml-2">kWp</span>
                </div>
              </motion.div>

              <div className="grid grid-cols-2 gap-3 mt-7">
                <ResultTile
                  icon={Coins}
                  label="Ersparnis / Jahr"
                  value={result ? `CHF ${fmt(result.annual_savings_chf)}` : "–"}
                  testid="calc-result-savings"
                />
                <ResultTile
                  icon={Leaf}
                  label="CO₂-Reduktion"
                  value={result ? `${fmt(result.co2_reduction_kg / 1000)} t` : "–"}
                  testid="calc-result-co2"
                />
                <ResultTile
                  icon={Clock}
                  label="Amortisation"
                  value={result ? `${result.payback_years} J.` : "–"}
                  testid="calc-result-payback"
                />
                <ResultTile
                  icon={TrendingUp}
                  label="Gewinn / 20 J."
                  value={result ? `CHF ${fmt(result.twenty_year_profit_chf)}` : "–"}
                  testid="calc-result-profit"
                  highlight
                />
              </div>

              <dl className="mt-6 space-y-2 text-sm">
                <div className="flex justify-between gap-4">
                  <dt className="text-white/55">Jahresertrag</dt>
                  <dd className="font-medium" data-testid="calc-result-yield">
                    {result ? `${fmt(result.annual_yield_kwh)} kWh` : "–"}
                  </dd>
                </div>
                <div className="flex justify-between gap-4">
                  <dt className="text-white/55">davon Eigenverbrauch</dt>
                  <dd className="font-medium" data-testid="calc-result-selfuse">
                    {result ? `${fmt(result.self_consumption_kwh)} kWh` : "–"}
                  </dd>
                </div>
                <div className="flex justify-between gap-4">
                  <dt className="text-white/55">Investition inkl. MWST</dt>
                  <dd className="font-medium" data-testid="calc-result-investment">
                    {result ? `CHF ${fmt(result.investment_chf)}` : "–"}
                  </dd>
                </div>
              </dl>

              <div className="mt-5 text-xs text-white/50 leading-relaxed">
                Indikative Berechnung auf Basis Schweizer Durchschnittswerte
                (Strompreis 30 Rp./kWh, Einspeisetarif 10 Rp./kWh, ca. 6 m² Dach
                je kWp). Verbindliche Werte nach Dachprüfung vor Ort.
              </div>

              <Button
                asChild
                data-testid="calc-cta-offer"
                className="mt-6 w-full bg-[#EAB308] hover:bg-[#CA9A06] text-[#1A1D20] rounded-full h-12 font-semibold"
              >
                <a href="#kontakt">Detail-Offerte anfordern</a>
              </Button>
            </div>
          </div>
        </div>

        <div ref={chartRef} className="mt-6">
          {result && chartInView ? (
            <Suspense fallback={<ChartSkeleton />}>
              <AmortisationChart
                investment={result.investment_chf}
                annualSavings={result.annual_savings_chf}
                paybackYears={result.payback_years}
              />
            </Suspense>
          ) : (
            <ChartSkeleton />
          )}
        </div>
      </div>
    </section>
  );
}

/** Platzhalter in Chart-Höhe – verhindert Layout-Sprünge beim Nachladen. */
function ChartSkeleton() {
  return (
    <div
      aria-hidden="true"
      className="rounded-2xl bg-[#1A1D20] h-[460px] sm:h-[500px] relative overflow-hidden"
    >
      <div className="absolute -bottom-24 -left-16 w-72 h-72 sun-ring opacity-30" />
    </div>
  );
}

function ResultTile({ icon: Icon, label, value, testid, highlight }) {
  return (
    <div
      data-testid={testid}
      className={`rounded-xl p-4 border ${
        highlight
          ? "bg-[#EAB308]/10 border-[#EAB308]/30"
          : "bg-white/5 border-white/10"
      }`}
    >
      <div className="flex items-center gap-2 text-[10px] uppercase tracking-wider text-white/60">
        <Icon size={12} /> {label}
      </div>
      <div
        className={`mt-2 font-display font-bold text-xl ${
          highlight ? "text-[#EAB308]" : "text-white"
        }`}
      >
        {value}
      </div>
    </div>
  );
}
